import { useState } from 'react';
import { useOrgs } from '@/contexts/OrgsContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { LoadingButton } from '@/components/LoadingButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateOrgSettings, getGetOrgSettingsQueryKey } from '@/api/control/control-plane/control-plane';
import { useGetOrganization, useUpdateOrganization, getGetOrganizationQueryKey } from '@/api/auth/organizations/organizations';
import { getGetMeOrganizationsQueryKey } from '@/api/auth/self/self';
import { getAvailableRegions, getRegionLabel, getRegionNatIp } from '@/constants/regions';
import { getCountryLabel } from '@/constants/countries';
import { CountryCombobox } from '@/components/orgs/CountryCombobox';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Organization settings card component
 * Displays and manages organization-level preferences and defaults
 */
export function OrgSettingsCard() {
  const { selectedOrganizationId, orgSettings, isLoadingOrgSettings } = useOrgs();
  const { t, language } = useLanguage();
  const [defaultRegion, setDefaultRegion] = useState<string>('');
  const [billingCountry, setBillingCountry] = useState<string>('');
  // Null until the user edits the name, so the field tracks the loaded organization name
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const updateOrgSettingsMutation = useUpdateOrgSettings();
  const updateOrganizationMutation = useUpdateOrganization();

  // Fetch organization details to get billing country
  const { data: orgDetailsResponse, isLoading: isLoadingOrgDetails } = useGetOrganization(
    selectedOrganizationId ?? '',
    {
      query: {
        enabled: !!selectedOrganizationId,
      },
    }
  );
  const orgDetails = orgDetailsResponse?.data ?? null;

  // Check if billing country is already set (cannot be changed once set)
  const hasBillingCountry = !!orgDetails?.billingCountry;

  const currentOrganizationName = organizationName ?? orgDetails?.name ?? '';

  /**
   * Handle org settings update
   */
  const handleSaveOrgSettings = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOrganizationId) {
      toast.error(t.settings.orgSettings.noOrganizationSelected);
      return;
    }

    const trimmedName = currentOrganizationName.trim();
    if (orgDetails && !trimmedName) {
      toast.error(t.settings.orgSettings.organizationNameRequired);
      return;
    }

    try {
      // Update the organization when the name changed or the billing country is set for the first time
      const isNameChanged = !!orgDetails && trimmedName !== orgDetails.name;
      const isSettingBillingCountry = !!billingCountry && !hasBillingCountry;
      if (orgDetails && (isNameChanged || isSettingBillingCountry)) {
        await updateOrganizationMutation.mutateAsync({
          orgId: selectedOrganizationId,
          data: {
            name: trimmedName,
            billingCountry: isSettingBillingCountry ? billingCountry : undefined,
          },
        });

        // Refresh org details and the membership list that feeds the organization switcher
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetOrganizationQueryKey(selectedOrganizationId),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetMeOrganizationsQueryKey(),
          }),
        ]);
      }

      // Update org settings (default region)
      await updateOrgSettingsMutation.mutateAsync({
        orgId: selectedOrganizationId,
        data: {
          defaultRegion: defaultRegion === 'none' ? undefined : defaultRegion || undefined,
        },
      });

      // Invalidate and refetch org settings
      await queryClient.invalidateQueries({
        queryKey: getGetOrgSettingsQueryKey(selectedOrganizationId),
      });

      toast.success(t.settings.orgSettings.updateSuccess);

      // Clear the form fields after successful save
      setDefaultRegion('');
      setBillingCountry('');
      setOrganizationName(null);
    } catch (error) {
      console.error('Failed to update organization settings:', error);
      toast.error(t.settings.orgSettings.updateError);
    }
  };

  /**
   * Get current default region value
   */
  const getCurrentDefaultRegion = () => {
    if (defaultRegion) return defaultRegion;
    return orgSettings?.defaultRegion ?? 'none';
  };

  const isLoading = isLoadingOrgSettings || isLoadingOrgDetails;
  const isSaving = updateOrgSettingsMutation.isPending || updateOrganizationMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.settings.orgSettings.title}</CardTitle>
        <CardDescription>
          {t.settings.orgSettings.description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => { void handleSaveOrgSettings(e); }} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="organizationName">{t.settings.orgSettings.organizationName}</Label>
            <p className="text-sm text-muted-foreground">
              {t.settings.orgSettings.organizationNameDescription}
            </p>
            <Input
              id="organizationName"
              value={currentOrganizationName}
              onChange={(e) => { setOrganizationName(e.target.value); }}
              disabled={isLoading || isSaving}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="billingCountry">{t.settings.orgSettings.billingCountry}</Label>
            <p className="text-sm text-muted-foreground">
              {hasBillingCountry
                ? t.settings.orgSettings.billingCountryDescriptionSet
                : t.settings.orgSettings.billingCountryDescriptionNotSet}
            </p>
            {hasBillingCountry ? (
              <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-1 text-sm">
                {getCountryLabel(orgDetails.billingCountry ?? '', language)}
              </div>
            ) : (
              <CountryCombobox
                id="billingCountry"
                value={billingCountry}
                onChange={setBillingCountry}
                disabled={isLoading || isSaving}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="defaultRegion">{t.settings.orgSettings.defaultRegion}</Label>
            <p className="text-sm text-muted-foreground">
              {t.settings.orgSettings.defaultRegionDescription}
            </p>
            <Select
              value={getCurrentDefaultRegion()}
              onValueChange={setDefaultRegion}
              disabled={isLoading || isSaving}
            >
              <SelectTrigger id="defaultRegion" className="w-full">
                <SelectValue placeholder={t.settings.orgSettings.noDefaultRegion} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t.settings.orgSettings.noDefaultRegion}</SelectItem>
                {getAvailableRegions().map((region) => {
                  const natIp = getRegionNatIp(region);
                  return (
                    <SelectItem key={region} value={region}>
                      <span className="flex items-center gap-2">
                        <span>{getRegionLabel(region)}</span>
                        {natIp && (
                          <span className="font-mono text-xs text-muted-foreground">{natIp}</span>
                        )}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end pt-4">
            <LoadingButton
              type="submit"
              disabled={isLoading}
              isLoading={isSaving}
            >
              {t.settings.orgSettings.save}
            </LoadingButton>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
